import type { ReadonlyURLSearchParams } from 'next/navigation';
import { validIcons } from '@brahma/components';
import { urlType } from '$modules/dashboard/helpers/manage-account/types';
import type { TFunction } from 'i18next';
import { useClickStream, EventType } from '@brahma/hooks';
import { ApplicantPortalOption } from '$constants/applicantPortalOption';
import { useOptionVisibility } from '$hooks/use-option-visibility';
export const getRouteConfigByPath = (pathname: string, query: ReadonlyURLSearchParams) => {
  const queryParam = Object.fromEntries(query.entries());
  const routePath = pathname.split('/').filter(segment => segment !== '').pop() ?? '';
  if (routePath === 'notification-preferences') {
    return queryParam.title;
  } else {
    return routePath;
  }
};
export function GetAPDashboardWebMenuItemList(t: TFunction<string | string[], string>, handleClick: {
  (url: string): void;
  (arg0: urlType): any;
}, handleSupportClick: {
  (): void;
}, handleTrackSupportTicketClick: {
  (): void;
}) {
  const {
    trackEvent
  } = useClickStream();
  const {
    isVisible
  } = useOptionVisibility();
  return [...(isVisible(ApplicantPortalOption.MANAGE_PROFILE) ? [{
    iconName: validIcons.user01,
    text: t('dashboardHeader.viewProfile'),
    onClick: () => {
      trackEvent({
        eventType: EventType.BUTTON_CLICK,
        data: {
          clickText: t('dashboardHeader.viewProfile')
        }
      });
      handleClick(urlType.userProfile);
    },
    divider: true
  }] : []), ...(isVisible(ApplicantPortalOption.SETTINGS) ? [{
    iconName: validIcons.settings1,
    text: t('dashboardHeader.manageAccount'),
    onClick: () => {
      trackEvent({
        eventType: EventType.BUTTON_CLICK,
        data: {
          clickText: t('dashboardHeader.manageAccount')
        }
      });
      handleClick(urlType.manageAccount);
    }
  }] : []), ...(isVisible(ApplicantPortalOption.VIEW_TRANSACTIONS) ? [{
    iconName: validIcons.switchHorizontal,
    text: t('dashboardHeader.allTransactions'),
    onClick: () => {
      trackEvent({
        eventType: EventType.BUTTON_CLICK,
        data: {
          clickText: t('dashboardHeader.allTransactions')
        }
      });
      handleClick(urlType.transactions);
    },
    divider: true
  }] : []), {
    iconName: validIcons.layersTwo01,
    text: t('dashboardHeader.aboutUs'),
    onClick: () => {
      trackEvent({
        eventType: EventType.BUTTON_CLICK,
        data: {
          clickText: t('dashboardHeader.aboutUs')
        }
      });
      handleClick(urlType.aboutUsWhoWeAre);
    }
  }, {
    iconName: validIcons.asterisk02,
    text: t('dashboardHeader.termsAndConditions'),
    onClick: () => {
      trackEvent({
        eventType: EventType.BUTTON_CLICK,
        data: {
          clickText: t('dashboardHeader.termsAndConditions')
        }
      });
      handleClick(urlType.termsAndConditions);
    }
  }, {
    iconName: validIcons.fileQuestion2,
    text: t('dashboardHeader.privacyPolicy'),
    onClick: () => {
      trackEvent({
        eventType: EventType.BUTTON_CLICK,
        data: {
          clickText: t('dashboardHeader.privacyPolicy')
        }
      });
      handleClick(urlType.privacyPolicy);
    },
    divider: true
  }, {
    iconName: validIcons.messageQuestionSquare,
    text: t('dashboardHeader.faq'),
    onClick: () => {
      trackEvent({
        eventType: EventType.BUTTON_CLICK,
        data: {
          clickText: t('dashboardHeader.faq')
        }
      });
      handleClick(urlType.faq);
    }
  }, ...(isVisible(ApplicantPortalOption.SUPPORT) ? [{
    iconName: validIcons.helpCircleNew,
    text: t('dashboardHeader.raiseSupportTicket'),
    onClick: () => {
      trackEvent({
        eventType: EventType.BUTTON_CLICK,
        data: {
          clickText: t('dashboardHeader.raiseSupportTicket')
        }
      });
      handleSupportClick();
    }
  }, {
    iconName: validIcons.helpCircleNew,
    text: t('dashboardHeader.trackSupportTicket'),
    onClick: () => {
      trackEvent({
        eventType: EventType.BUTTON_CLICK,
        data: {
          clickText: t('dashboardHeader.trackSupportTicket')
        }
      });
      handleTrackSupportTicketClick();
    }
  }] : [])];
}
export interface WebHeaderMenuItem {
  iconName?: validIcons;
  text: string;
  onClick: () => void;
  id: string;
}
export const AVAILABLE_LANGUAGES = {
  en: 'en',
  ar: 'ar'
};
export function GetLanguageMenuItemList(t: TFunction<string | string[], string>, handleLanguageChange: (lang: string) => void): WebHeaderMenuItem[] {
  const {
    trackEvent
  } = useClickStream();
  return [{
    text: t('dashboardHeader.arabicNative'),
    onClick: () => {
      trackEvent({
        eventType: EventType.BUTTON_CLICK,
        data: {
          clickText: t('dashboardHeader.arabicNative')
        }
      });
      handleLanguageChange(AVAILABLE_LANGUAGES.ar);
    },
    id: AVAILABLE_LANGUAGES.ar
  }, {
    text: t('dashboardHeader.englishNative'),
    onClick: () => {
      trackEvent({
        eventType: EventType.BUTTON_CLICK,
        data: {
          clickText: t('dashboardHeader.englishNative')
        }
      });
      handleLanguageChange(AVAILABLE_LANGUAGES.en);
    },
    id: AVAILABLE_LANGUAGES.en
  }];
}
export const updateURLForLocaleChange = (newLang: string, lang: string | string[], setLoading?: (loading: boolean) => void) => {
  if (typeof window === 'undefined') return;
  const currentPath = window.location.pathname;
  const queryString = window.location.search;
  const regex = new RegExp(`^(.*?)/${lang}/`);
  const newPath = currentPath.replace(regex, `/${newLang}/`);
  const updatedURL = `${newPath}${queryString}`;
  if (setLoading) {
    setLoading(true);
  }
  window.location.href = updatedURL;
};